import {useEffect, useRef} from 'react';
import {useThree} from '@react-three/fiber';
import {xrStore} from 'lib';
import {useARStore} from 'stores';
import {checkTouchedModel} from 'utils';

type StateType = 'none' | 'checkTouchedModel' | 'move' | 'rotate';

const useInputStates = () => {
  const scene = useThree(s => s.scene);
  const modelActive = useARStore(s => s.modelActive);
  const setModelActive = useARStore(s => s.setModelActive);
  const isModelInteractiveRef = useARStore(s => s.isModelInteractiveRef);

  const stateRef = useRef<StateType>('none');
  const touchedModelIdRef = useRef<string>(undefined);

  useEffect(() => {
    const controller = new AbortController();
    xrStore.getState().session?.addEventListener(
      'inputsourceschange',
      async e => {
        if (!isModelInteractiveRef.current) {
          return;
        }

        const inputSources = e.session.inputSources;
        const addedLength = e.added.length;
        const removedLength = e.removed.length;

        const firstIn = inputSources.length === 1 && addedLength === 1;
        const firstOut = inputSources.length === 0 && removedLength === 1;
        const twoIn = inputSources.length === 2 && addedLength === 2;
        const oneHoldOneIn = inputSources.length === 2 && addedLength === 1;
        const allOut = inputSources.length === 0;

        if (firstIn) {
          stateRef.current = 'checkTouchedModel';
          const touchedModelId = await checkTouchedModel(scene);
          touchedModelIdRef.current = touchedModelId;
          if (!touchedModelId) {
            return;
          }
          stateRef.current = 'move';
          setModelActive({id: touchedModelId});
          return;
        }

        if (firstOut) {
          if (stateRef.current === 'checkTouchedModel' && touchedModelIdRef.current === '' && !modelActive.justPlaced) {
            touchedModelIdRef.current = undefined;
            setModelActive({id: '', justPlaced: false});
          }
          setModelActive({justPlaced: false});
          stateRef.current = 'none';
          return;
        }

        if (twoIn) {
          stateRef.current = 'rotate';
          return;
        }

        if (oneHoldOneIn) {
          stateRef.current = 'rotate';
          return;
        }

        if (allOut) {
          stateRef.current = 'none';
          return;
        }
      },
      {signal: controller.signal},
    );
    return () => {
      controller.abort();
    };
  }, [scene, modelActive]);

  return {stateRef};
};

export default useInputStates;
